import { z } from "zod/v4"
import { SubscriptionItemGrantMasterId, SubscriptionItemGrantDistributionChunkId } from "../../id"
import { DocumentSnapshotLike } from "../../firestore"
import { CreatedAt, UpdatedAt } from "../system"

export enum SubscriptionItemGrantDistributionChunkStatus {
  Pending = "pending",
  Delivering = "delivering",
  Completed = "completed",
}

export const SubscriptionItemGrantDistributionChunkResult = z.object({
  successful_count: z.number().describe("成功した件数"),
  failed_count: z.number().describe("失敗した件数"),
  skipped_count: z.number().describe("スキップした件数"),
  failed_user_ids: z.array(z.string()).describe("失敗したユーザーIDリスト"),
  skipped_user_ids: z.array(z.string()).describe("スキップしたユーザーIDリスト"),
  completed_at: z.number().describe("完了時刻 (UNIX Timestamp)"),
})

export type SubscriptionItemGrantDistributionChunkResult = z.infer<typeof SubscriptionItemGrantDistributionChunkResult>

export const SubscriptionItemGrantDistributionChunkMeta = z.object({
  user_ids: z.array(z.string()).describe("対象ユーザーIDリスト"),
  user_count: z.number().describe("ユーザー数"),
  master_id: z.string().transform(SubscriptionItemGrantMasterId).describe("親のマスターID"),
})

export type SubscriptionItemGrantDistributionChunkMeta = z.infer<typeof SubscriptionItemGrantDistributionChunkMeta>

export const SubscriptionItemGrantDistributionChunkDocumentBody = SubscriptionItemGrantDistributionChunkMeta.extend({
  status: z.enum(SubscriptionItemGrantDistributionChunkStatus).describe("処理ステータス"),
  visible_start_at: z.number().describe("いつ以降処理可能か"),
  try_count: z.number().describe("処理試行回数"),
  result: SubscriptionItemGrantDistributionChunkResult.optional().describe("処理結果"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type SubscriptionItemGrantDistributionChunkDocumentBody = z.infer<typeof SubscriptionItemGrantDistributionChunkDocumentBody>

export type SubscriptionItemGrantDistributionChunk = SubscriptionItemGrantDistributionChunkDocumentBody & {
  id: SubscriptionItemGrantDistributionChunkId
}

export class SubscriptionItemGrantDistributionChunkDocuments {
  static toDocumentData(chunk: SubscriptionItemGrantDistributionChunk): object {
    return {
      ...chunk,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SubscriptionItemGrantDistributionChunk | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SubscriptionItemGrantDistributionChunkId(doc.id),
      user_ids: data["user_ids"],
      user_count: data["user_count"],
      master_id: SubscriptionItemGrantMasterId(data["master_id"]),
      status: data["status"],
      visible_start_at: data["visible_start_at"] ?? 0,
      try_count: data["try_count"] ?? 0,
      result: data["result"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
